/**
 * SummaryStep.api.js
 *
 * Talks to the `/generateDietPlan` endpoint (functions/index.js) that
 * persists everything shown on Screen 12 — the same data the Summary
 * sections render — before the Generate Diet Plan button is allowed to
 * advance the wizard. Kept as its own file, same as every other step's
 * .constants/.styles/.validation split, so SummaryStep.js stays focused
 * on rendering and doesn't grow its own fetch/error-parsing logic inline.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://us-central1-website-a1336.cloudfunctions.net/api";
// ^ TODO: point this at wherever the rest of the app already configures its
// Cloud Functions base URL (a shared api client, firebaseConfig.js, etc.).
// This constant is a stand-in — none of the uploaded files exposed an
// existing one, so wire it to the real value before shipping.

const GENERATE_DIET_PLAN_ENDPOINT = `${API_BASE_URL}/generateDietPlan`;

/**
 * @param {object} payload - Same shape index.js's /generateDietPlan
 *   validates: { sessionId, basicInfo, goal, lifestyle, activity, medical,
 *   foodPreferences, likes, dislikes, mealSchedule, womensHealth, isFemale }.
 * @returns {Promise<object>} The parsed success response, matching
 *   response.ts's Response shape: { code, errorMessage, data: Array<any> }.
 * @throws {Error} With a short, user-presentable message — on a network
 *   failure, a non-2xx response, or a response body that isn't JSON.
 */
export async function submitAssessment(payload) {
  let response;
  try {
    response = await fetch(GENERATE_DIET_PLAN_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (networkError) {
    // fetch itself threw — offline, DNS failure, CORS, etc. There's no
    // response to inspect, so this is the only branch with a hardcoded
    // message rather than one read off the server.
    throw new Error("Couldn't reach the server. Check your connection and try again.");
  }

  let data = null;
  try {
    data = await response.json();
  } catch {
    // Body wasn't JSON (e.g. a proxy error page) — fall through and let
    // response.ok decide the outcome below.
  }

  if (!response.ok) {
    // index.js's /generateDietPlan replies { code, errorMessage, data }
    // (response.ts's Response shape) — errorMessage is a generic label
    // ("Invalid parameters", "Internal Server Error"), while the specific,
    // per-field reason lives in data[0].msg on validation failures
    // (express-validator's errors.array()). Prefer the specific one when
    // it exists; fall back to the generic label, then a hardcoded string.
    const serverMessage = data?.data?.[0]?.msg || data?.errorMessage || null;
    throw new Error(serverMessage || "Something went wrong. Please try again.");
  }

  return data;
}